import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { useReducedMotion } from "@/lib/motion";
import { ConciergeRequestModal, type ConciergePrefill, type ConciergeSource } from "./ConciergeRequestModal";
import conciergeHeroImage from "@assets/stock_images/concierge_classic_car_3.jpg";

interface ConciergeBannerProps {
  variant: "hero" | "inline";
  prefill?: ConciergePrefill;
  /** Optional CSS class overrides (margin/spacing) for the wrapper. */
  className?: string;
}

export function ConciergeBanner({ variant, prefill, className }: ConciergeBannerProps) {
  const { t, i18n } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const source: ConciergeSource = variant;

  const handleOpen = () => {
    track("concierge_request_opened", {
      source,
      result_count: prefill?.resultCount,
      location: prefill?.location ?? null,
      locale: i18n.language || null,
    });
    setOpen(true);
  };

  if (variant === "hero") {
    return (
      <>
        {/*
          Hero variant tidy-up (user feedback: "ugly and cluttered").
          Previously the headline + bullets sat in a left column and the
          CTA was squeezed into a narrow right column, which made the
          button text wrap into an awkward "Find me / the / perfect /
          car" circle. We now use a single left-aligned column with the
          CTA as a normal pill below the body, no whitespace wrapping,
          and slightly more breathing room. Bullets render as a horizontal
          inline row separated by dots for a calmer rhythm.
        */}
        <motion.section
          aria-labelledby="concierge-hero-title"
          initial={reducedMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={cn(
            "col-span-full mt-6 relative overflow-hidden rounded-2xl border border-border p-6 sm:p-10",
            className,
          )}
          data-testid="concierge-hero"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${conciergeHeroImage})` }}
          />
          {/* Slightly softer right-side fade so the car peeks through
              more on desktop without compromising left-side legibility. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30 sm:from-black/80 sm:via-black/55 sm:to-black/5"
          />
          <div className="relative max-w-xl">
            {/* material: intentional — frosted-white eyebrow chip on a dark photo hero; white bg required for legibility over the dark gradient */}
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              {t("concierge.banner.eyebrow")}
            </span>
            <h2
              id="concierge-hero-title"
              className="mt-4 text-2xl font-bold leading-tight text-white sm:text-3xl"
            >
              {t("concierge.banner.hero_title")}
            </h2>
            <p className="mt-3 text-sm text-white/80 sm:text-base">
              {t("concierge.banner.hero_subtitle")}
            </p>
            <ul className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-white/85 sm:text-sm">
              <li className="flex items-center gap-2">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white/70" />
                {t("concierge.banner.bullet_curated")}
              </li>
              <li className="flex items-center gap-2">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white/70" />
                {t("concierge.banner.bullet_response")}
              </li>
              <li className="flex items-center gap-2">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white/70" />
                {t("concierge.banner.bullet_no_obligation")}
              </li>
            </ul>
            <div className="mt-6">
              <button
                type="button"
                onClick={handleOpen}
                className="primary-action cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-3 text-sm font-semibold shadow-lg transition-colors duration-200"
                data-testid="concierge-hero-cta"
              >
                {t("concierge.banner.cta")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </motion.section>
        <ConciergeRequestModal
          open={open}
          onOpenChange={setOpen}
          source={source}
          prefill={prefill}
        />
      </>
    );
  }

  return (
    <>
      <motion.section
        aria-labelledby="concierge-inline-title"
        initial={reducedMotion ? false : { opacity: 0, y: 12 }}
        whileInView={reducedMotion ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className={cn(
          "col-span-full overflow-hidden rounded-2xl border border-border bg-gradient-to-r from-primary/8 via-background to-background p-5 sm:p-6",
          className,
        )}
        data-testid="concierge-inline"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Sparkles className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h3
                id="concierge-inline-title"
                className="text-base font-bold leading-snug text-foreground"
              >
                {t("concierge.banner.inline_title")}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("concierge.banner.inline_subtitle")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleOpen}
            className="cursor-pointer inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-primary bg-background px-5 py-2.5 text-sm font-semibold text-primary transition-colors duration-200 hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            data-testid="concierge-inline-cta"
          >
            {t("concierge.banner.cta")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </motion.section>
      <ConciergeRequestModal
        open={open}
        onOpenChange={setOpen}
        source={source}
        prefill={prefill}
      />
    </>
  );
}

export default ConciergeBanner;
